/**
 * Typed fetch wrapper for the Veda Union API.
 *
 * - Resolves the path against `API_ORIGIN` (a no-op on the website, which is
 *   same-origin; absolute for the packaged app — see lib/platform.ts)
 * - Website / PWA: sends + receives the HttpOnly session cookie
 * - Packaged app: sends `Authorization: Bearer` instead, and omits credentials
 *   entirely so no cookie is implied cross-origin
 * - Sets JSON Content-Type for body-bearing requests
 * - Throws an `ApiError` (with `status` + parsed body) on non-2xx
 * - Returns parsed JSON on success
 *
 * Use directly when you need full control; auth hooks in `lib/auth.ts` wrap this
 * behind react-query mutations and queries.
 */
import { authToken, isLocalOrigin, resolveUrl } from './platform';

export interface ApiErrorBody {
  error?: string;
  message?: string;
  // Zod / @hono/zod-validator emits this shape on 400
  success?: false;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | undefined;

  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Headers every API call carries, plus the credentials mode for this build.
 *
 * `X-Vedaunion-Client: app` is what makes the server hand back a session token
 * in the login/register body (see server/src/routes/auth.ts) — the app has no
 * other way to receive one.
 */
/**
 * The absolute URL for an API path.
 *
 * Exported because a multipart upload and a download LINK cannot go through
 * `apiFetch` — one needs FormData, the other is an `href` the browser follows —
 * and a bare relative path resolves to the WebView's own origin in the packaged
 * app rather than to the API.
 */
export function resolveApiUrl(path: string): string {
  return resolveUrl(path);
}

/** The auth header the app needs; empty on the web, where a cookie carries it. */
export function apiAuthHeaders(): Record<string, string> {
  const h = apiHeaders();
  delete h['Content-Type'];
  return h;
}

export function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  if (isLocalOrigin) {
    headers['X-Vedaunion-Client'] = 'app';
    const token = authToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** Cookies for the website; nothing at all for the packaged app. */
export const apiCredentials: RequestCredentials = isLocalOrigin ? 'omit' : 'include';

/** `fetch`, but pointed at the API origin and carrying this build's auth. */
export function apiRequest(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(resolveUrl(path), {
    ...init,
    credentials: apiCredentials,
    headers: apiHeaders(init.headers as Record<string, string> | undefined),
  });
}

export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const headers = apiHeaders();
  const init: RequestInit = {
    method,
    credentials: apiCredentials,
    headers,
    signal,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(resolveUrl(path), init);
  } catch (err) {
    throw new ApiError(0, 'Network error. Please check your connection.', undefined);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  let parsed: unknown = undefined;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      parsed = await res.json();
    } catch {
      /* empty / invalid JSON */
    }
  }

  if (!res.ok) {
    const body = (parsed as ApiErrorBody | undefined) ?? undefined;
    const message =
      body?.message ?? body?.error ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }

  return parsed as T;
}
